<script setup>
    import { ref, reactive, onMounted, computed, watch } from 'vue'
    import NavigationBar from '../components/NavigationBar.vue'
    import HeaderComponent from '../components/HeaderComponent.vue'
    import CallApi  from '../utils/callApi.js'
    import dayjs from 'dayjs';

    import { Line } from 'vue-chartjs'
    import {
      Chart as ChartJS,
      LineElement,
      PointElement,
      LinearScale,
      CategoryScale
    } from 'chart.js'

    ChartJS.register(
      LineElement,
      PointElement,
      LinearScale,
      CategoryScale
    )

    // ChartJSの登録
    ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale)

    // ダッシュボード情報
    const dashboardInfo = reactive({
        // 現在の登録人数
        registUserNum: 0,
        // 当月の認証回数
        monthAuthCount: 0,
        // 当月の認証成功回数
        monthSuccessAuthCount: 0,
        // 当月の認証失敗回数
        monthFailAuthCount: 0,
        // システムログ一覧
        syserrAuthLogs: [],
        // システムログ表示制御
        showAllLogs: false,
        // ページネーション
        currentPage: 1,
})
    const currentMonth = ref(null) // 現在選択している年月
    const nowDate = dayjs() // 現在日時

    const pageSize = ref(10); // 1ページの件数
    const isLoading = ref(false); // ローディング画面表示フラグ

    // 当月取得
    const setMonth = async () => {
        // YYYY-MM形式
        currentMonth.value = nowDate.format('YYYY-MM')
    }

    // 現在の登録人数を取得
    const getUserCount = async () => {
        try {
            const data = await CallApi.getAxiosUserCount();
            return data.count
        } catch (error) {
            console.error('通信エラー:', error);
            return 0
        }
    }

    // 当月の認証回数を取得
    const getAuthCount = async () => {
        try {
            // 該当月の範囲を取得
            const monthDate = dayjs(currentMonth.value)
            const start = monthDate.startOf('month').format('YYYYMMDD')
            const end = monthDate.endOf('month').format('YYYYMMDD')
            const params = {
                "startDate": start,
                "endDate": end
            }
            const data = await CallApi.getAxiosAuthInfo(params);

            // 日毎のリクエスト回数を合計      
            const sum = data.req_infos.reduce((acc, item) => {
                acc.total += Number(item.total_count || 0);
                acc.success += Number(item.success_count || 0);
                acc.fail += Number(
                    item.fail_count ?? (item.total_count - item.success_count)
                ) || 0;
                return acc;
            }, { total: 0, success: 0, fail: 0 });

            dashboardInfo.monthAuthCount = sum.total;
            dashboardInfo.monthSuccessAuthCount = sum.success;
            dashboardInfo.monthFailAuthCount = sum.fail;

        } catch (error) {
            console.error('通信エラー:', error);
        }
    }
    // YYYY/MM/DD HH:mm:ss形式に変換
    const formatViewTime = (dateString)  => {
        if (!dateString) return "-"
        return `${dateString.slice(0, 4)}/${dateString.slice(4, 6)}/${dateString.slice(6, 8)}
                 ${dateString.slice(8, 10)}:${dateString.slice(10, 12)}:${dateString.slice(12, 14)}`
    }

    // 認証エラーログを取得
    const getErrAuthLogs = async () => {
        try {
            const params = {
                "kind":"all",
                "status":"fail"
            }
            const data = await CallApi.getAxiosAuthLogs(params);
            dashboardInfo.syserrAuthLogs = data.log_infos.map(log_info => ({
                code:log_info?.response_status || "",
                message:log_info?.err_msg || "",
                timestamp:formatViewTime(log_info.request_time),
            }))
            // 日付順にソート
            dashboardInfo.errAuthLogs = await dashboardInfo.syserrAuthLogs.sort((element, nextElement) => {
                        return (element['timestamp'] > nextElement['timestamp']) ? -1 : 1;
                    })

        } catch (error) {
            console.error('通信エラー:', error);
        }
    }
    // ログを全件表示
    const viewAllErrLog = async () => {
        dashboardInfo.showAllLogs = true;
    }

    // 認証エラーログの表示
    const displayedLogs = computed(() => {
        if(!dashboardInfo.showAllLogs &&  dashboardInfo.syserrAuthLogs && dashboardInfo.syserrAuthLogs.length > 0)  {
            return dashboardInfo.syserrAuthLogs.slice(0, 5);
        } 
        // 全件表示 + ページネーション
        const start = (dashboardInfo.currentPage - 1) * pageSize.value;
        const end = start + pageSize.value;

        return dashboardInfo.syserrAuthLogs.slice(start, end);
    });

    // 認証エラーログの件数からページネーションの設定
    const totalPages = computed(() => {
        
        const total = dashboardInfo.syserrAuthLogs?.length || 0;
        return Math.ceil(
            total / pageSize.value
        );
    });

    // 1か月の認証回数推移のグラフ描画
    const getDailyAuthCountChartData = async () => {
        const labels = [];
        const data = [];
        try{
            const start = dayjs().subtract(29, 'day').format('YYYYMMDD');
            const end = dayjs().format('YYYYMMDD');
            const params = {
                startDate: start,
                endDate: end
            };

            const response = await CallApi.getAxiosAuthInfo(params);
            const reqInfos = response.req_infos ?? [];
            for (let i = 29; i >= 0; i--) {
                const date = dayjs().subtract(i, 'day');
                const key = date.format('YYYYMMDD');
                const label = date.format('M/D');
                labels.push(label);

                const found = reqInfos.find(item => String(item.date) === key);
                data.push(found ? Number(found.total_count || 0) : 0);
            }
        } catch (error) {
            console.error('通信エラー:', error);
        }
        const chartData = {
            labels,
            datasets: [
                {
                    label: '認証回数(回)',
                    data,
                    fill: false,
                    borderColor: '#ff1493',
                    backgroundColor: '#ff1493',
                    borderWidth: 3,
                    tension: 0.3,
                    pointRadius: 3
                }
            ]
        }
        return chartData
    }
    // グラフ表示オプション設定
    const getDailyAuthCountChartOptions = (labels) => {
        const chartOptions = { 
            responsive: true,
            maintainAspectRatio: true, 
            plugins: {
                legend: {
                    display: true,
                    position: 'top',
                    align: 'start',
                    labels: {
                        usePointStyle: false,
                        boxWidth: 40,
                        boxHeight: 2
                    }
                }
            },
            scales: {
                x: {
                    grid: {
                        display: false
                    },
                    ticks: {
                        autoSkip: false,
                        callback(index, ticks) {
                            if (
                                index === 0 ||
                                index === ticks.length - 1 ||
                                index % 7 === 0
                            ) {
                                return labels[index];
                            }
                            return '';
                        }
                    }
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        callback: function(value) {
                            return Number.isInteger(value) ? value : '';
                        }
                    }
                }
            }
        }
        return chartOptions
    }

    const chartData = ref(null); // グラフ表示用データ
    // グラフ表示オプション
    const chartOptions = computed(() => 
        getDailyAuthCountChartOptions(chartData.value?.labels || [])
    );

    // 1000単位で,区切りにする表示形式(Ex,30,000)に変換
    const formatNumber = (value) => {
        if (value == null) return '-'
        return Number(value).toLocaleString()
    }

    // ページネーション表示判定
    const showPagination = computed(() => {
        return (
            dashboardInfo.showAllLogs && dashboardInfo.syserrAuthLogs?.length >
            pageSize.value
        );
    });

    onMounted(async () => {
        // ローディング画面の表示
        isLoading.value = true

        // 当月の設定
        await setMonth();
        // 登録人数の取得
        dashboardInfo.registUserNum = await getUserCount();
        // 認証回数の取得
        await getAuthCount();
        // 認証エラーログの取得
        await getErrAuthLogs();
        // グラフ表示用データの取得
        chartData.value = await getDailyAuthCountChartData();

        // ローディング画面の停止
        isLoading.value = false;
    })

    // 表示する月が変更されたら、認証回数を再取得する
    watch(currentMonth, async () => {
        // ローディング画面の表示
        isLoading.value = true
        // 認証回数を再取得
        await getAuthCount()
        // ローディング画面の停止
        isLoading.value = false;
    })
    // currentPage が totalPages を超えたら補正する
    watch(totalPages, (newTotalPages) => {
        if (dashboardInfo.currentPage > newTotalPages) {
            dashboardInfo.currentPage = Math.max(1, newTotalPages);
        }
    })
    
</script>

<template>
    <HeaderComponent></HeaderComponent>
    <NavigationBar></NavigationBar>
    <main>
        <div class="mainView">
            <div>
                <div class="titleArea">
                    <p class="title">ダッシュボード</p>
                    <div class="monthpicker-wrapper">
                        <img src="../assets/calendar.png" class="calendar-icon">
                        <input type="month" v-model="currentMonth"  class="monthpickerArea">
                    </div>
                </div>
                <div class="viewArea">
                    <div class="viewDashBoardArea">
                        <div id="viewArea1">
                            <p class="viewDashBoardTitle">①現在の登録人数</p>
                            <div id="registUserImageArea">
                                <img src="..//assets/dashboard_registNum.png" id="registUserImage">
                                 <p><span class="viewDashBoardText">{{ formatNumber(dashboardInfo.registUserNum) }}</span>人</p>
                            </div>
                        </div>
                        <div id="viewArea2">
                            <p class="viewDashBoardTitle"   >②当月の認証回数</p>
                            <div id="monthAuthCountArea">
                                <img src="../assets/dashboard_authCount.png" id="monthAuthCountImage">
                                <p><span class="viewDashBoardText">{{ formatNumber(dashboardInfo.monthAuthCount) }}</span>回</p>
                            </div>
                        </div>
                        <div id="viewArea3">
                            <p class="viewDashBoardTitle">③当月の認証成功数/失敗数</p>
                            <div id="authCountArea">
                                <div id="authSuccessArea">
                                    <img src="../assets/dashboard_authSuccessCount.png" id="authSuccessImage">
                                    <div>
                                        <p class="authCountText">成功数</p>
                                        <p><span class="viewDashBoardText">{{ formatNumber(dashboardInfo.monthSuccessAuthCount) }}</span>回</p>
                                    </div>
                                </div>
                                <div id="authFailureArea">
                                    <img src="../assets/dashboard_authFailCount.png" id="authFailureImage">
                                    <div>
                                        <p class="authCountText">失敗数</p>
                                        <p><span class="viewDashBoardText">{{ formatNumber(dashboardInfo.monthFailAuthCount) }}</span>回</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="viewDashBoardArea">
                        <div id="viewArea4">
                            <p class="viewDashBoardTitle">④直近1ヵ月の認証回数推移</p>
                            <div id="dailyAuthCountArea">
                                <div id="dailyAuthCountGraph">
                                    <Line v-if="chartData" :data="chartData" :options="chartOptions" />
                                </div>
                            </div>
                        </div>
                        <div id="viewArea5">
                            <p class="viewDashBoardTitle">⑤直近のエラー内容(認証エラーログ)</p>
                            <div id="errorLogArea">
                                <div id="errorLogTable">
                                    <table class="commonTable">
                                        <thead>
                                            <tr>
                                                <th>発生日時</th>
                                                <th>エラーコード</th>
                                                <th>エラー内容</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(log, key) in displayedLogs" :key="key">
                                                <td id="errLogTimestamp">{{log.timestamp }}</td>
                                                <td id="errLogCode">{{ log.code }}</td>
                                                <td>{{ log.message }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <p 
                                        id="viewAllErrLog" 
                                        v-if="!dashboardInfo.showAllLogs && dashboardInfo.syserrAuthLogs.length > 5" 
                                        @click="viewAllErrLog"
                                    >すべてのエラーログを表示 > </p>
                                    <div v-if="showPagination" class="pagination-area">
                                        <div class="pagination">
                                            <button
                                                class="page-btn"
                                                @click="dashboardInfo.currentPage--"
                                                :disabled="dashboardInfo.currentPage === 1"
                                            >←</button>
                                            <button
                                                v-for="page in totalPages"
                                                :key="page"
                                                class="page-btn"
                                                :class="{ active: dashboardInfo.currentPage === page }"
                                                @click="dashboardInfo.currentPage = page"
                                            >{{ page }}</button>
                                            <button
                                                class="page-btn"
                                                @click="dashboardInfo.currentPage++"
                                                :disabled="dashboardInfo.currentPage === totalPages"
                                            >→</button>
                                        </div>   
                                    </div> 
                                </div>
                                     
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="isLoading" class="loading-overlay">
            <div class="loading-box">
                <p>Loading...</p>
                <div class="spinner"></div>
            </div>
        </div>
    </main>

</template>

<style scoped>
    .titleArea{
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }
    .monthpicker-wrapper {
        position: relative;
        display: inline-block;
    } 
    .monthpickerArea {
        width: 120px;
        height: 50px;

        padding-left: 10px;
        padding-right: 30px;

        border: 1px solid #d9d9d9;
        border-radius: 6px;
        background: #fff;

        font-size: 15px;
        color: #333;
        font-weight: bold;

        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;

        cursor: pointer;
    }

    .calendar-icon {
        position: absolute;
        left: 10px;
        top: 50%;
        width: 24px;
        height: 24px;
        transform: translateY(-50%);
        pointer-events: none;
    }
    .monthpickerArea::-webkit-calendar-picker-indicator {
         opacity: 0;
        position: absolute;
        right: 0;
        width: 100%;
        height: 100%;
        cursor: pointer;
        }

    .monthpicker-wrapper::after {
        content: "▼";
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 10px;
        color: #666;
        pointer-events: none;
    }
    .monthpickerDisplay {
        margin-left: 6px;
        pointer-events: none;
    }
    
    .monthpickerArea:hover {
        border-color: #bfbfbf;
    }

    .monthpickerArea:focus {
        outline: none;
        border-color: #4096ff;
        box-shadow: 0 0 0 2px rgba(64, 150, 255, 0.15);
    }

    .viewArea {
        width: 100%;
        box-sizing: border-box;
    }
    .viewDashBoardArea {
        display: flex;
        justify-content: space-between;
        width: 100%;
        padding: 0 0 20px 0;
        gap: 2%;
        align-items: flex-start;
    }
    #viewArea1, #viewArea2, #viewArea3{
        width: 30%;
        padding: 5px;
        background: white;
        border-radius: 10px;
        border: 1px solid #969292;
    }
    #viewArea3{
        width: 60%;
    }
    #registUserImageArea, #monthAuthCountArea {
        display: flex;
        align-items: center;
        gap: 30px;
        justify-content: center;
    }
   
    #viewArea4,#viewArea5{
        width: 50%;
        padding: 20px 0px 0px 20px;
        background: white;
        border-radius: 10px;
        border: 1px solid #969292;
    }
    #viewArea1,
    #viewArea2 {
        position: relative;
        min-height: 160px;
        padding: 20px;
        box-sizing: border-box;
    }
    #viewArea1 > p,
    #viewArea2 > p {
        margin: 0 0 1.5rem 0;
    }
    #registUserImageArea,
    #monthAuthCountArea {
        position: absolute;
        left: 20px;
        bottom: 20px;
    }
    #viewArea1 > div:last-child,
    #viewArea2 > div:last-child {
        position: absolute;
        right: 20px;
        bottom: 20px;
    }
    .viewDashBoardTitle {
        font-size: 18px;
        font-weight: bold;
        text-align: left;
    }
    .viewDashBoardText {
        font-size: 30px;
        font-weight: bold;
        text-align: right;
    }
    #registUserImage, #monthAuthCountImage, #authSuccessImage, #authFailureImage{
        width: 70px;
        height: 70px;
    }
    #authCountArea {
        display: flex;
        align-items: center;
        padding-top: 20px;
    }
    #authSuccessArea {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        border-right: 1px dotted #999;
    }
    #authFailureArea {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    #dailyAuthCountArea{
        width: 80%;
        margin: 0 auto;
        padding:20px;
        padding-bottom:0;
    }
    #dailyAuthCountArea canvas {
        width: 100% !important;
        height: 100% !important;
    }
    #errorLogArea{
        display:block;
    }
    #errorLogTable{
        width: 95%;
        overflow-x: auto;
        margin: 30px 0;
    }
    #errLogTimestamp{
        width:30%;
    }
    #errLogCode{
        width:10%;
    }
    #viewAllErrLog{
        color:#ff1493;
        text-align: right;
        cursor: pointer;
    }
    @media (max-width: 1150px) {
        .dashboard {
            margin-left: 0;
            padding-left: 5%;
        }
        .titleArea {
            flex-direction: column;
            align-items: flex-start;
        }
        .monthpickerArea {
            margin-top: 10px;
        }

        .viewDashBoardArea {
            margin-top:0;
            flex-direction: column;
        }
        #viewArea1, #viewArea2, #viewArea3, #viewArea4, #viewArea5 {
            width: 100%;
            box-sizing: border-box;
            margin-bottom: 10px;
        }
    }

    @media (max-width: 768px) {
        .viewDashBoardArea {
            flex-direction: row;
            flex-wrap: wrap;
            gap: 10px;
        }

        #viewArea1,
        #viewArea2 {
            width: calc(50% - 5px);
            min-width: 0;
            margin-bottom: 0;
        }
        #viewArea3 {
            width: 100%;
        }
    }
</style>